import * as Sentry from '@sentry/nextjs'
import Router from 'next/router'

import { tracesSampler } from '@/lib/sentry/performanceTracing'

/**
 * Times the CookieHub banner for first-time visitors on traced routes, so step 5 of the paid
 * journey, cookie banner answered (see `docs/data-flows/paid-journey-steps.md`), shows in Sentry
 * next to the page loads.
 *
 * One transaction per document, named after the route (so `tracesSampler` and dashboards treat it
 * like the page) with op `ui.consent`. It starts at navigation start and has two child spans:
 * - `consent.banner.ready`: navigation start until CookieHub has initialised. CookieHub emits no
 *   "banner shown" event; for a visitor with no stored choice the banner renders on initialise.
 * - `consent.banner.answer`: banner ready until the visitor answers it.
 * The transaction carries a `consent_choice` attribute. If the page is hidden before the visitor
 * answers, the transaction ends there as `never_answered`.
 *
 * Returning visitors (already answered) see no banner and send nothing.
 */
const OP = 'ui.consent'
const INITIALISE_EVENT = 'cookiehub_onInitialise'
const STATUS_CHANGE_EVENT = 'cookiehub_onStatusChange'

export type ConsentChoice = 'allow_all' | 'deny_all' | 'partial' | 'never_answered'

/** Analytics and marketing are the categories that decide what we can track. */
const readChoice = (): ConsentChoice => {
  const analytics = window.cookiehub?.hasConsented('analytics') ?? false
  const marketing = window.cookiehub?.hasConsented('marketing') ?? false
  if (analytics && marketing) return 'allow_all'
  if (!analytics && !marketing) return 'deny_all'
  return 'partial'
}

/** Seconds since the epoch for a `performance.now()` reading, as Sentry span times expect. */
const toTimestamp = (now: number): number => (performance.timeOrigin + now) / 1000

/** Timing must never break the banner or the page: on any Sentry failure the step is dropped. */
const guarded =
  <A extends unknown[]>(fn: (...args: A) => void) =>
  (...args: A): void => {
    try {
      fn(...args)
    } catch {
      // Dropped: a lost timing is better than a consent flow that throws.
    }
  }

export const trackCookieBannerTiming = guarded((): void => {
  if (typeof window === 'undefined' || typeof performance === 'undefined') return

  const route = Router.router?.pathname ?? window.__NEXT_DATA__?.page
  if (!route || tracesSampler({ name: route }) === 0) return

  const onInitialise = guarded(() => {
    document.removeEventListener(INITIALISE_EVENT, onInitialise)
    if (window.cookiehub?.hasAnswered()) return

    const readyAt = performance.now()
    const transaction = Sentry.startInactiveSpan({
      name: route,
      op: OP,
      forceTransaction: true,
      startTime: toTimestamp(0),
    })
    if (!transaction) return

    Sentry.withActiveSpan(transaction, () => {
      Sentry.startInactiveSpan({ name: 'CookieHub ready', op: 'consent.banner.ready', startTime: toTimestamp(0) })?.end(
        toTimestamp(readyAt),
      )
    })

    let finished = false
    const finish = guarded((choice: ConsentChoice) => {
      if (finished) return
      finished = true
      document.removeEventListener(STATUS_CHANGE_EVENT, onAnswer)
      document.removeEventListener('visibilitychange', onHidden)
      const answeredAt = performance.now()
      if (choice !== 'never_answered') {
        Sentry.withActiveSpan(transaction, () => {
          Sentry.startInactiveSpan({
            name: 'Banner answered',
            op: 'consent.banner.answer',
            startTime: toTimestamp(readyAt),
          })?.end(toTimestamp(answeredAt))
          // Journey step 5, cookie banner answered: banner ready until answered (see `docs/data-flows/paid-journey-steps.md`).
          Sentry.setMeasurement('journey.step5_cookie_banner_answered', Math.round(answeredAt - readyAt), 'millisecond')
        })
      }
      transaction.setAttribute('consent_choice', choice)
      transaction.setStatus(choice === 'never_answered' ? 'cancelled' : 'ok')
      transaction.end(toTimestamp(answeredAt))
    })

    const onAnswer = () => finish(readChoice())
    const onHidden = () => {
      if (document.visibilityState === 'hidden') finish('never_answered')
    }

    document.addEventListener(STATUS_CHANGE_EVENT, onAnswer)
    document.addEventListener('visibilitychange', onHidden)
  })

  document.addEventListener(INITIALISE_EVENT, onInitialise)
})
